/**
 * auctioneerAudio.js - Adam's audio singleton for BOTH draft formats.
 *
 * Uses data URIs instead of Blob URLs - eliminates all revocation/state
 * corruption issues on iOS Safari. One persistent Audio element, unlocked
 * via the "LET'S DRAFT!" gesture.
 *
 * [ADAM AUDIO foundation - founder-approved pre-build review] This module
 * now owns a SERIALIZED QUEUE. Before, every entry point assigned
 * `audio.src = ...` the moment it was called, so a new line cut Adam off
 * mid-sentence - that assignment WAS the clipping (the round-rollover
 * "Round N starts in 3, 2, 1" being the visible victim). Now:
 *
 *   • `enqueue()` is the only way audio starts. Clips play ONE AT A TIME
 *     in arrival order. A new enqueue NEVER interrupts a playing clip.
 *   • The returned Promise (and `whenDone(id)`) resolves when THAT clip
 *     ends, so callers can gate visuals on audio - the reveal/countdown
 *     sequencing builds on this.
 *   • `stopAll()` is the ONLY hard interrupt (mute, recap dismissal,
 *     commissioner pause). `playLine` / `playFromBase64` are thin
 *     enqueue wrappers, so existing callers keep working - but their
 *     semantics are now "queue behind", not "cut in".
 *   • Backup protection: a queue that only ever grows would make Adam
 *     narrate the past. Each entry carries its activity type as a
 *     `group`; a newer line SUPERSEDES the pending (not-yet-started)
 *     lines its beat makes moot (SOLD drops pending going-once/twice,
 *     a new nomination drops pending bid chatter, a newer pick drops the
 *     older pick line). Anything in `CRITICAL_GROUPS` is never dropped -
 *     that is what guarantees the round-rollover line always plays.
 *
 * Deleted with this change (no dead code): `playStaticClip` (zero call
 * sites - it was imported by DraftContext and never used) plus the
 * `prefetchStaticClip` / `_staticClips` machinery that only fed it. Its
 * job - instant, clock-locked lines - is the formulaic audio cache's.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
import { diag, diagError } from "../utils/diag";

let _audio   = null;
let _ready   = false;
let _unlockInstalled = false;
let _notReadyLogged = false;
let _muted   = false;
let _volume  = 1;

// Pending entries (not yet started) + the one currently playing.
const _queue = [];
let _current = null;
// Resolver for the in-flight clip so stopAll() can complete it cleanly
// instead of leaving the Promise (and the pump) pending forever.
let _currentDone = null;
let _seq = 0;

// Hard ceiling on pending lines so a fast sequence can't back up into
// Adam narrating stale beats. Oldest NON-critical line is evicted first.
const MAX_PENDING = 4;

/**
 * Lines that must always be heard in full. Never superseded, never
 * evicted by the pending cap.
 */
export const CRITICAL_GROUPS = new Set([
  "ROUND_START",                 // snake round rollover
  "AUCTIONEER_ROUND_START",      // auction round rollover
  "AUCTIONEER_SOLD",
  "AUCTIONEER_SALE_UNDONE",
  "AUCTIONEER_PAUSED",
  "AUCTIONEER_RESUMED",
  "AUCTIONEER_DRAFT_COMPLETE",
  "DRAFT_STARTED_WELCOME",
  "COUNTDOWN",                   // lobby countdown beats
  "REVEAL",                      // draft-order reveal lines
]);

/**
 * `group -> groups whose PENDING entries this line makes moot`.
 * Only pending entries are dropped; a playing clip always finishes.
 */
export const SUPERSEDES = {
  AUCTIONEER_SOLD:        ["AUCTIONEER_GOING_ONCE", "AUCTIONEER_GOING_TWICE", "AUCTIONEER_BID"],
  AUCTIONEER_NOMINATION:  ["AUCTIONEER_BID", "AUCTIONEER_GOING_ONCE", "AUCTIONEER_GOING_TWICE", "AUCTIONEER_ON_CLOCK"],
  AUCTIONEER_GOING_TWICE: ["AUCTIONEER_GOING_ONCE", "AUCTIONEER_BID"],
  AUCTIONEER_GOING_ONCE:  ["AUCTIONEER_BID"],
  AUCTIONEER_BID:         ["AUCTIONEER_BID"],
  AUCTIONEER_ON_CLOCK:    ["AUCTIONEER_ON_CLOCK"],
  AUCTIONEER_NOMINATION_SKIPPED: ["AUCTIONEER_ON_CLOCK"],
  PICK_MADE:              ["PICK_MADE"],
};

// Debug log - last 6 events, visible in UI
const _log = [];
function dbg(msg) {
  _log.push(`${new Date().toLocaleTimeString("en",{hour12:false,hour:"2-digit",minute:"2-digit",second:"2-digit"})} ${msg}`);
  if (_log.length > 6) _log.shift();
}
export function getDebugLog() { return [..._log]; }

/** Minimal valid 1-sample silent WAV */
function makeSilentWav() {
  const b = new ArrayBuffer(46), v = new DataView(b);
  v.setUint32(0,  0x52494646, false);
  v.setUint32(4,  38,         true);
  v.setUint32(8,  0x57415645, false);
  v.setUint32(12, 0x666d7420, false);
  v.setUint32(16, 16,         true);
  v.setUint16(20, 1,          true);
  v.setUint16(22, 1,          true);
  v.setUint32(24, 44100,      true);
  v.setUint32(28, 88200,      true);
  v.setUint16(32, 2,          true);
  v.setUint16(34, 16,         true);
  v.setUint32(36, 0x64617461, false);
  v.setUint32(40, 2,          true);
  v.setInt16(44,  0,          true);
  return new Blob([b], { type: "audio/wav" });
}

function getAudio() {
  if (!_audio) {
    _audio = new Audio();
    _audio.preload = "auto";
    _audio.volume = _volume;
  }
  return _audio;
}

/**
 * Call synchronously inside a user gesture handler.
 * Plays a silent WAV to unlock the Audio element on iOS Safari.
 *
 * Anything enqueued before the gesture WAITS in the queue rather than
 * being dropped - the pump refuses to start until the element is
 * unlocked, then drains in order.
 */
export function prewarm() {
  if (_ready) return;
  dbg("prewarm start");
  const audio = getAudio();
  const url   = URL.createObjectURL(makeSilentWav());
  audio.src   = url;
  const done  = () => {
    URL.revokeObjectURL(url);
    _ready = true;
    dbg("prewarm done");
    _pump();
  };
  const p     = audio.play();
  if (p?.then) p.then(done).catch(done); else done();
}

/**
 * Unlock on the FIRST user gesture anywhere in the app.
 *
 * `prewarm()` was only called from a couple of specific places (the
 * commissioner's reveal-complete handler being the main one). Any tab that
 * never hit one of those — a manager who just sits in the lobby, a standalone
 * draft board — kept `_ready === false`, and because `_pump()` bails while not
 * ready, every line queued on that tab sat in the queue forever: Adam silent,
 * and any `await enqueue(...)` never resolving. That is what froze the
 * pre-draft ceremony. One idempotent listener removes the whole class of bug.
 */
export function installAudioUnlock() {
  if (typeof window === "undefined" || _unlockInstalled) return;
  _unlockInstalled = true;
  const unlock = () => {
    prewarm();
    if (_ready) removeListeners();
  };
  const removeListeners = () => {
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
    window.removeEventListener("touchstart", unlock, true);
  };
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
  window.addEventListener("touchstart", unlock, true);
}

export function isReady() { return _ready; }
export function isBusy()  { return _current !== null; }
export function queueLength() { return _queue.length; }
export function isMuted() { return _muted; }

/**
 * [ADAM AUDIO batch 2] The league every live synth belongs to. Without
 * it `/api/tts/speak` has nothing to resolve the voice from and falls
 * back to Voice 1 - which would put a Voice-2 league's live lines (the
 * reveal, recaps, any cache miss) in the wrong voice next to its
 * correctly-voiced cached clips. Set once per lobby / draft room mount.
 */
let _leagueId = null;

export function setAdamLeague(leagueId) {
  _leagueId = leagueId || null;
}

function _fetchTts(text) {
  return fetch(`${BACKEND}/api/tts/speak`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({
      text: String(text).slice(0, 500),
      league_id: _leagueId,
    }),
  })
    .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
    .then(({ audio_b64 }) => {
      if (!audio_b64) diagError("adam.tts_empty", { text_head: String(text).slice(0, 60) });
      return audio_b64;
    })
    .catch((err) => {
      diagError("adam.tts_fetch_failed", {
        error: String(err?.message || err),
        league_id: _leagueId,
        text_head: String(text).slice(0, 60),
      });
      throw err;
    });
}

/**
 * Start a synth NOW without queueing anything. Returns a promise of the
 * base64 clip (null on failure) to hand straight to `enqueue({ fetchPromise })`.
 *
 * Used by the draft-order reveal, which warms EVERY team line in parallel
 * the moment the reveal starts: the queue only ever pre-warms one line
 * ahead, and a 12-team reveal would otherwise stall between flips waiting
 * on ElevenLabs. The reveal must be gated by Adam's speech, never by
 * synth latency.
 */
export function prefetchTts(text) {
  return _fetchTts(text).catch(() => null);
}

/**
 * Play one base64 clip on the shared element. Resolves on ended / error
 * / timeout, or immediately when stopAll() interrupts it.
 * `offsetSec > 0` seeks first (mid-clip reconnect).
 */
function _playBase64(audio_b64, offsetSec = 0, onProgress = null) {
  return new Promise((resolve) => {
    if (!audio_b64) { resolve(); return; }
    const audio = getAudio();
    const dataUri = `data:audio/mpeg;base64,${audio_b64}`;
    let rafId = null;

    const done = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("loadedmetadata", onMeta);
      clearTimeout(timer);
      _currentDone = null;
      resolve();
    };
    const onEnded = () => done();
    const onError = (e) => {
      dbg(`err: ${e?.message || "?"}`);
      diagError("adam.audio_element_error", { message: String(e?.message || "?") });
      done();
    };
    const onMeta = () => {
      if (offsetSec <= 0) return;
      const dur = Number.isFinite(audio.duration) ? audio.duration : 0;
      if (dur > 0 && offsetSec >= dur) { done(); return; }
      if (dur > 0) {
        try { audio.currentTime = Math.min(offsetSec, Math.max(0, dur - 0.05)); } catch (_e) { /* ignore */ }
      }
    };

    audio.addEventListener("ended", onEnded, { once: true });
    audio.addEventListener("error", onError, { once: true });
    audio.addEventListener("loadedmetadata", onMeta, { once: true });
    const timer = setTimeout(() => {
      dbg("timeout");
      diagError("adam.play_timeout", { ms: 30000 });
      done();
    }, 30000);
    _currentDone = done;

    audio.src = dataUri;
    audio.play().catch((err) => {
      dbg(`play() rejected: ${err.name}`);
      // NotAllowedError here means the browser blocked playback for want of a
      // user gesture — Adam is silent and it is NOT a TTS fault.
      diagError("adam.play_rejected", { name: String(err?.name || "?"), message: String(err?.message || "") });
      done();
    });

    // [ADAM AUDIO batch 2 item 2] Progress ticks off the clip's OWN
    // currentTime. The countdown paces its 3/2/1 visual from this, so
    // decode latency self-corrects instead of accumulating.
    if (onProgress && typeof requestAnimationFrame === "function") {
      const tick = () => {
        onProgress(audio.currentTime || 0);
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }
  });
}

async function _pump() {
  if (!_ready && _queue.length && !_notReadyLogged) {
    _notReadyLogged = true;
    diagError("adam.pump_blocked_not_ready", {
      queued: _queue.length,
      groups: _queue.map((e) => e.group).slice(0, 8),
    });
  }
  if (_current || _muted || !_ready) return;
  const entry = _queue.shift();
  if (!entry) return;
  _current = entry;
  try {
    const b64 = entry.audio_b64 || (await (entry._fetch || _fetchTts(entry.text)));
    if (!_muted) {
      dbg(`play ${entry.group}: "${String(entry.text || "b64").slice(0, 24)}"`);
      await _playBase64(b64, entry.offsetSec || 0, entry.onProgress || null);
    }
  } catch (err) {
    dbg(`fetch err: ${err.message}`);
  } finally {
    _current = null;
    entry.resolve();
    _prefetchHead();
    _pump();
  }
}

/** Warm the next text-only line's synth while the current clip plays. */
function _prefetchHead() {
  const next = _queue[0];
  if (next && !next.audio_b64 && !next._fetch && next.text) {
    next._fetch = _fetchTts(next.text).catch(() => null);
  }
}

function _applySupersede(group) {
  const victims = SUPERSEDES[group];
  if (!victims?.length) return;
  for (let i = _queue.length - 1; i >= 0; i -= 1) {
    const e = _queue[i];
    if (CRITICAL_GROUPS.has(e.group)) continue;
    if (victims.includes(e.group)) {
      _queue.splice(i, 1);
      e.resolve();
      dbg(`superseded ${e.group}`);
    }
  }
}

function _enforcePendingCap() {
  while (_queue.length > MAX_PENDING) {
    const idx = _queue.findIndex((e) => !CRITICAL_GROUPS.has(e.group));
    if (idx === -1) return; // all critical - never drop
    const [dropped] = _queue.splice(idx, 1);
    dropped.resolve();
    dbg(`evicted ${dropped.group}`);
  }
}

/**
 * Queue one Adam clip. Returns a Promise that resolves when THAT clip
 * finishes (or is superseded / muted away), so a caller can gate a
 * visual on it.
 *
 *   enqueue({ id?, group, text?, audio_b64?, offsetSec?, fetchPromise? })
 *
 * `audio_b64` plays instantly (pre-rendered); `text` is synthesized via
 * `/api/tts/speak`; `fetchPromise` is a synth already in flight from
 * `prefetchTts` (bulk-warmed sequences). `group` is the activity type and
 * drives the supersede + critical policy above.
 */
export function enqueue({
  id,
  group = "GENERIC",
  text = null,
  audio_b64 = null,
  offsetSec = 0,
  onProgress = null,
  fetchPromise = null,
} = {}) {
  if (!text && !audio_b64 && !fetchPromise) return Promise.resolve();
  if (_muted) return Promise.resolve();
  const entryId = id || `adam_${Date.now()}_${_seq++}`;
  if (_current?.id === entryId || _queue.some((e) => e.id === entryId)) {
    return Promise.resolve();
  }
  _applySupersede(group);
  let resolve;
  const promise = new Promise((res) => { resolve = res; });
  _queue.push({
    id: entryId, group, text, audio_b64, offsetSec, onProgress, resolve, promise,
    // An already-in-flight synth (see `prefetchTts`) - the pump awaits it
    // instead of starting its own fetch when the clip's turn comes.
    _fetch: fetchPromise,
  });
  _enforcePendingCap();
  if (!_current) _prefetchHead();
  _pump();
  return promise;
}

/** Promise for a specific queued/playing clip, or resolved if unknown. */
export function whenDone(id) {
  if (_current?.id === id) return _current.promise;
  const found = _queue.find((e) => e.id === id);
  return found ? found.promise : Promise.resolve();
}

/** Fetch + queue a spoken line. Resolves when it finishes playing. */
export function playLine(text, group = "GENERIC") {
  return enqueue({ group, text });
}

/** Queue a pre-rendered base64 MP3. Resolves when it finishes playing. */
export function playFromBase64(audio_b64, group = "GENERIC") {
  return enqueue({ group, audio_b64 });
}

/**
 * [Founder Item 4 - Feb 2026] Queue a pre-rendered clip starting at an
 * offset (seconds) - a client reconnecting mid-clip hears the same
 * moment everyone else does. Resolves silently when the offset is past
 * the clip's end.
 */
export function playFromBase64AtOffset(audio_b64, offsetSec = 0) {
  return enqueue({ group: "RECONNECT", audio_b64, offsetSec });
}

/** Drop every pending line. The playing clip is untouched. */
export function clearPending({ keepCritical = true } = {}) {
  for (let i = _queue.length - 1; i >= 0; i -= 1) {
    if (keepCritical && CRITICAL_GROUPS.has(_queue[i].group)) continue;
    const [dropped] = _queue.splice(i, 1);
    dropped.resolve();
  }
}

/**
 * The ONLY hard interrupt: stop the playing clip and drop everything
 * pending. Mute, recap dismissal, and commissioner pause use this.
 */
export function stopAll() {
  const audio = _audio;
  if (audio) { audio.pause(); audio.src = ""; }
  clearPending({ keepCritical: false });
  if (_currentDone) {
    const done = _currentDone;
    _currentDone = null;
    try { done(); } catch (_e) { /* swallow */ }
  }
}

/**
 * Mute gate. Muting stops Adam immediately and clears the queue;
 * unmuting resumes future lines (it does not replay dropped ones).
 */
export function setMuted(muted) {
  const next = !!muted;
  if (next === _muted) return;
  _muted = next;
  if (_muted) stopAll();
  else _pump();
}

export function setAudioVolume(vol) {
  const v = Math.max(0, Math.min(1, vol));
  if (_audio) _audio.volume = v;
  // Store for new audio elements created after prewarm
  _volume = v;
}

export function getAudioVolume() {
  return _audio?.volume ?? _volume;
}
